<template>
  <transition name="ef-toast-slide">
    <div
      v-if="openState"
      class="fixed bottom-4 left-1/2 -translate-x-1/2 right-4 md:left-auto md:translate-x-0 z-[60] w-full max-w-md md:min-w-[260px] px-4 md:px-0"
      role="status"
      aria-live="polite"
    >
      <div :class="wrapperClass">
        <div class="shrink-0 pt-0.5" aria-hidden="true">
          <i :class="iconClass"></i>
        </div>
        <div class="flex-1 min-w-0 pl-1">
          <p class="text-sm leading-5 break-words">{{ message }}</p>
        </div>
        <!-- クローズボタンを追加 -->
        <button
          v-if="closable"
          type="button"
          class="shrink-0 ml-2 pt-0.5 hover:opacity-70 transition-opacity"
          aria-label="通知を閉じる"
          @click="close"
        >
          <i class="fa-solid fa-xmark text-sm"></i>
        </button>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
defineOptions({ name: "EfToast" });
import { computed, onMounted, onBeforeUnmount, watch, shallowRef } from "vue";
import type { ToastVariant, ToastProps, ToastEmits } from "./types.d";
import { toastColorScheme } from "@/styles/color-scheme";

// 定数定義
const TRANSITION_DURATION_MS = 200;
const DEFAULT_DURATION_MS = 4000;

// 定数をエクスポートしてCSS v-bind()で使用
defineExpose({ TRANSITION_DURATION_MS });

const props = withDefaults(defineProps<ToastProps>(), {
  open: false,
  message: "検討中リストに追加しました",
  variant: "primary",
  durationMs: DEFAULT_DURATION_MS,
  closable: false,
});

const emit = defineEmits<ToastEmits>();

const isOpen = shallowRef(props.open);
watch(
  () => props.open,
  (v) => {
    if (typeof v === "boolean") isOpen.value = v;
  },
);

const openState = computed(() => isOpen.value);

let timer: number | null = null;
const armTimer = () => {
  if (props.durationMs && props.durationMs > 0) {
    clearTimer();
    timer = window.setTimeout(() => close(), props.durationMs);
  } else if (props.durationMs !== null && props.durationMs <= 0) {
    console.warn("Toast: durationMs must be positive number or null");
  }
};
const clearTimer = () => {
  if (timer != null) {
    clearTimeout(timer);
    timer = null;
  }
};

const close = () => {
  isOpen.value = false;
  emit("update:open", false);
  clearTimer();
};

onMounted(() => {
  if (isOpen.value) armTimer();
});

onBeforeUnmount(() => {
  clearTimer();
});

watch(openState, (v) => {
  if (v) armTimer();
});

const wrapperClass = computed(() => {
  const base = [
    "ef-toast",
    "w-full",
    "min-h-10",
    "rounded-md",
    "border",
    "px-3",
    "py-2",
    "shadow-lg",
    "flex",
    "items-center",
    "gap-2.5",
  ];

  // カラーアーキテクチャ（統一カラー設計）
  const colorSystem = toastColorScheme;

  let colors = colorSystem[props.variant];

  if (!colors) {
    console.warn(
      `Toast: Invalid variant "${props.variant}". Using "info" as fallback.`,
    );
    colors = colorSystem.info;
  }

  return [...base, colors.bg, colors.color];
});

const iconClass = computed(() => {
  const iconMap: Record<ToastVariant, string> = {
    primary: "fa-solid fa-check",
    secondary: "fa-solid fa-bell",
    success: "fa-solid fa-circle-check",
    info: "fa-solid fa-circle-info",
    warning: "fa-solid fa-triangle-exclamation",
    error: "fa-solid fa-circle-exclamation",
  };

  const colors = toastColorScheme[props.variant];

  if (!colors) {
    return `${iconMap.info} ${toastColorScheme.info.color}`;
  }

  return `${iconMap[props.variant] || iconMap.info} ${colors.color}`;
});
</script>

<style scoped>
.ef-toast-slide-enter-active,
.ef-toast-slide-leave-active {
  transition:
    opacity v-bind(TRANSITION_DURATION_MS + "ms") ease,
    transform v-bind(TRANSITION_DURATION_MS + "ms") ease;
}
.ef-toast-slide-enter-from {
  opacity: 0;
  transform: translateY(16px) translateX(-50%);
}
.ef-toast-slide-enter-to {
  opacity: 1;
  transform: translateY(0) translateX(-50%);
}
.ef-toast-slide-leave-from {
  opacity: 1;
  transform: translateY(0) translateX(-50%);
}
.ef-toast-slide-leave-to {
  opacity: 0;
  transform: translateY(16px) translateX(-50%);
}

/* PC表示時は右から左のアニメーション */
@media (min-width: 768px) {
  .ef-toast-slide-enter-from {
    opacity: 0;
    transform: translateX(16px);
  }
  .ef-toast-slide-enter-to {
    opacity: 1;
    transform: translateX(0);
  }
  .ef-toast-slide-leave-from {
    opacity: 1;
    transform: translateX(0);
  }
  .ef-toast-slide-leave-to {
    opacity: 0;
    transform: translateX(16px);
  }
}
</style>
